import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Button,
  ListItem,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Link, useHistory } from 'react-router-dom';
import { Place } from 'features/places/places.types';
import { removeAddressCountry } from 'shared/utils';
import { PlaceLocationPinIcon, getPlaceIcon } from 'features/mapping/icons';
import { usePlaces } from 'features/places/hooks/usePlaces';
import { placeLocationToMapLocation } from 'features/places/places.utils';
import { buildLocationState, useCurrentLocation } from 'hooks/useLocationState';
import { useReleaseFlag } from 'hooks/useReleaseFlags';

const useStyles = makeStyles()((theme) => ({
  link: {
    color: theme.palette.accent.main,
    fontWeight: theme.typography.fontWeightBold,
    fontFamily: theme.typography.fontFamily,
    padding: '6px 8px',
    textDecoration: 'none',
    textTransform: 'uppercase',
    minWidth: 64,
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export const SavedPlaces = (): JSX.Element => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { places } = usePlaces();
  const pinImprovements = useReleaseFlag('pin_drop_improvements');

  // @todo remove when removing 'pin_drop_improvements' release flag
  const handlePlaceClick = (location: Place): void => {
    const mapLocation = placeLocationToMapLocation(location);
    history.replace('/', mapLocation);
  };

  const locationState = buildLocationState(useCurrentLocation());

  return (
    <Grid container spacing={2} sx={{ width: '100%' }}>
      <Grid size={12}>
        <List dense>
          {places.map((place) => {
            const icon = getPlaceIcon(place.icon);
            return (
              <ListItem
                key={place.id}
                disablePadding
                secondaryAction={
                  <Link
                    to={{
                      pathname: pinImprovements
                        ? `/place/${place.id}/edit`
                        : `/edit_place/${place.id}`,
                      state: pinImprovements ? locationState : {},
                      hash: pinImprovements ? undefined : 'allow-back',
                    }}
                    className={classes.link}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {t('common.edit')}
                  </Link>
                }
                sx={{ '.MuiListItemSecondaryAction-root': { right: 0 } }}
              >
                <ListItemButton
                  component={pinImprovements ? Link : Button}
                  to={pinImprovements ? `/place/${place.id}` : undefined}
                  onClick={
                    pinImprovements ? undefined : () => handlePlaceClick(place)
                  }
                  disableGutters
                  sx={{ '&.MuiListItemButton-root': { paddingRight: 8 } }}
                >
                  <ListItemIcon
                    sx={(theme) => ({ minWidth: theme.spacing(4) })}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <img
                        src={PlaceLocationPinIcon.data}
                        alt="Place"
                        width={PlaceLocationPinIcon.width}
                        height={PlaceLocationPinIcon.height}
                      />
                      <img
                        src={icon.data}
                        alt={place.name}
                        width={icon.width}
                        height={icon.height}
                        style={{
                          position: 'absolute',
                          left: `calc(50% - ${icon.width / 2}px)`,
                          top: `calc(50% - ${icon.height}px)`,
                        }}
                      />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography>
                        <b>{place.name}</b>
                      </Typography>
                    }
                    secondary={removeAddressCountry(
                      place.address.formattedAddress || place.address.name,
                    )}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Grid>

      <Grid size={12}>
        <Button
          fullWidth
          size="large"
          to={{
            pathname: pinImprovements ? `/place/create` : `/add_place`,
            state: pinImprovements ? locationState : {},
            hash: pinImprovements ? undefined : 'allow-back',
          }}
          component={Link}
        >
          {t('places.button')}
        </Button>
      </Grid>
    </Grid>
  );
};
